import React from 'react';

const DashboardSkeleton = () => {
  return (
    <div className="bg-transparent w-full h-full flex flex-col overflow-y-auto overflow-x-hidden animate-pulse">
      <div className="max-w-[1700px] mx-auto px-2 sm:px-4 py-2 sm:py-2 flex-1 w-full flex flex-col mb-2">
        
        {/* Top Section: Metrics + 3 Info Cards */}
        <div className="flex flex-col mb-2">
          
          {/* Row 1: KPI Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2 sm:gap-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 flex items-center gap-3 min-h-[70px]">
                <div className="w-10 h-10 rounded-2xl bg-slate-100 flex shrink-0"></div>
                <div className="flex-1 min-w-0 flex flex-col justify-center">
                  <div className="h-2 w-16 bg-slate-100 rounded mb-1.5"></div>
                  <div className="h-5 w-12 bg-slate-200 rounded"></div>
                  <div className="h-2 w-20 bg-slate-50 rounded mt-1.5"></div>
                </div>
              </div>
            ))}
          </div>

          {/* Row 2: Leave Balance, Upcoming Holidays, Alerts */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-3 mt-2 sm:mt-3">
            
            {/* Leave Balance */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 sm:p-4 min-h-[250px] flex flex-col">
              <div className="flex items-center justify-between pb-3 border-b border-slate-100 gap-2">
                <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-slate-100 shrink-0"></div>
                  <div className="min-w-0">
                    <div className="h-4 w-24 bg-slate-200 rounded mb-1"></div>
                    <div className="h-2 w-32 bg-slate-100 rounded hidden xs:block"></div>
                  </div>
                </div>
                <div className="w-16 h-6 bg-slate-100 rounded-lg shrink-0"></div>
              </div>
              <div className="grid grid-cols-3 gap-1 sm:gap-2 mt-4 flex-1 items-center">
                {[1, 2, 3].map((i) => (
                  <div key={i} className={`text-center flex flex-col items-center gap-2 ${i === 2 ? 'border-x border-slate-200 px-1' : ''}`}>
                    <div className="mx-auto mb-1.5 w-7 h-7 sm:w-8 sm:h-8 rounded-xl bg-slate-50 shrink-0"></div>
                    <div className="h-6 w-8 sm:w-10 bg-slate-200 rounded"></div>
                    <div className="h-2 w-16 bg-slate-100 rounded mt-1"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Upcoming Holidays */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-3 sm:p-4 flex flex-col justify-start relative overflow-hidden h-full">
              <div className="relative z-10 flex flex-row items-center justify-between mb-3 gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-slate-100 shrink-0"></div>
                  <div className="min-w-0">
                    <div className="h-4 w-28 bg-slate-200 rounded mb-1"></div>
                    <div className="h-2 w-20 bg-slate-100 rounded hidden xs:block"></div>
                  </div>
                </div>
                <div className="w-16 h-6 bg-slate-100 rounded-lg shrink-0"></div>
              </div>
              <div className="relative z-10 flex-1 flex flex-col min-h-0 gap-2">
                <div className="h-12 w-full bg-slate-50 rounded-xl border border-slate-100"></div>
                <div className="h-12 w-full bg-slate-50 rounded-xl border border-slate-100"></div>
                <div className="h-12 w-full bg-slate-50 rounded-xl border border-slate-100 hidden sm:block"></div>
                <div className="mt-2 pt-2 border-t border-slate-100">
                  <div className="h-3 w-24 bg-slate-100 rounded"></div>
                </div>
              </div>
            </div>

            {/* Alerts */}
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm h-full flex flex-col overflow-hidden relative">
              <div className="p-3 sm:p-4 border-b border-transparent flex justify-between items-center bg-transparent shrink-0 relative z-10 gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-slate-100 shrink-0"></div>
                  <div className="h-4 w-16 bg-slate-200 rounded"></div>
                </div>
                <div className="w-16 h-6 bg-slate-100 rounded-lg shrink-0"></div>
              </div>
              <div className="px-3 sm:px-4 pb-3 sm:pb-4 flex-1 relative z-10 overflow-hidden flex flex-col gap-3">
                <div className="h-14 w-full bg-slate-50 rounded-xl border-l-4 border-slate-200"></div>
                <div className="h-14 w-full bg-slate-50 rounded-xl border-l-4 border-slate-200"></div>
                <div className="h-14 w-full bg-slate-50 rounded-xl border-l-4 border-slate-200"></div>
              </div>
            </div>

          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-2 sm:gap-3 mt-1 sm:mt-1">
          
          {/* Hours Worked Chart */}
          <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-200 shadow-sm p-3 sm:p-4 flex flex-col">
            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-slate-100 flex shrink-0"></div>
                <div>
                  <div className="h-4 w-40 bg-slate-200 rounded mb-1"></div>
                  <div className="h-2 w-48 bg-slate-100 rounded mt-0.5"></div>
                </div>
              </div>
              <div className="w-20 h-6 bg-slate-100 rounded-lg shrink-0"></div>
            </div>
            <div className="flex-1 min-h-[200px] flex flex-col bg-slate-50/50 rounded-xl border border-slate-100"></div>
          </div>

          {/* Project Allocation Chart */}
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-3 sm:p-4 flex flex-col justify-between">
            <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2 mb-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-xl bg-slate-100 flex shrink-0"></div>
                <div>
                  <div className="h-4 w-32 bg-slate-200 rounded mb-1"></div>
                  <div className="h-2 w-40 bg-slate-100 rounded mt-0.5"></div>
                </div>
              </div>
              <div className="w-20 h-6 bg-slate-100 rounded-lg shrink-0"></div>
            </div>
            <div className="flex-1 flex flex-col mt-1 min-h-[200px] bg-slate-50/50 rounded-xl border border-slate-100"></div>
          </div>
          
        </div>

      </div>
    </div>
  );
};

export default DashboardSkeleton;
